import { PatientData, GenerateNoteResponse, Medication } from '../types';

const API_BASE = '/api';

// Strip the 'id' field from medications before sending to API
function preparePatientData(data: PatientData): Omit<PatientData, 'medications'> & { medications: Omit<Medication, 'id'>[] } {
  return {
    ...data,
    medications: data.medications.map(({ name, dose, frequency }) => ({
      name,
      dose,
      frequency
    }))
  };
}

export async function generateNote(patientData: PatientData): Promise<GenerateNoteResponse> {
  const response = await fetch(`${API_BASE}/notes/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ patientData: preparePatientData(patientData) })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to generate note' }));
    throw new Error(error.error || 'Failed to generate note');
  }

  return response.json();
}

export async function downloadPdf(note: string, patientName: string): Promise<void> {
  const response = await fetch(`${API_BASE}/notes/pdf`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ note, patientName })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to generate PDF' }));
    throw new Error(error.error || 'Failed to generate PDF');
  }

  // Get the blob and trigger download
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `psychiatric-note-${patientName.replace(/\s+/g, '-').toLowerCase() || 'patient'}-${Date.now()}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export async function checkHealth(): Promise<{ status: string; timestamp: string }> {
  const response = await fetch(`${API_BASE}/health`);
  if (!response.ok) {
    throw new Error('API health check failed');
  }
  return response.json();
}
